import React from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Chip,
  Alert,
  AlertTitle,
  Stack,
  Tooltip
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { DataQuality } from '../types/api.types';

interface DataQualityIndicatorProps {
  quality: DataQuality;
}

const DataQualityIndicator: React.FC<DataQualityIndicatorProps> = ({
  quality
}) => {
  const { confidence = 0, sources = [], timestamp, missing_fields: missingFields = [] } = quality;
  // Determine quality level
  const getQualityLevel = () => {
    if (confidence >= 0.8) return { level: 'high', color: 'success', icon: <CheckCircleIcon /> };
    if (confidence >= 0.5) return { level: 'medium', color: 'warning', icon: <WarningIcon /> };
    return { level: 'low', color: 'error', icon: <ErrorIcon /> };
  };

  const qualityLevel = getQualityLevel();

  // Format data sources
  const formatSource = (source: string) => {
    const sourceMap: { [key: string]: string } = {
      // Old source names (for backward compatibility)
      'municipal_arcgis': 'Municipal GIS',
      'massgis_l3': 'MassGIS L3 Parcels',
      'web_scrape': 'Web Scraping',
      'pdf_parse': 'PDF Ordinances',
      'cached': 'Cached Data',
      'default': 'Default Values',
      // New source names from real-time data integration
      'l3': 'MassGIS L3 Parcels',
      'oauth2': 'Authenticated ArcGIS',
      'municipal': 'Municipal GIS',
      'web': 'Web Scraping',
      'pdf': 'PDF Ordinances',
      'market': 'Market Data APIs',
      'public': 'Public Data Sources',
      'ml_predictor': 'ML Predictions',
      'local_structured_fallback': 'Local Fallback'
    };
    return sourceMap[source] || source;
  };

  // Format timestamp
  const formatTimestamp = (ts: string) => {
    try {
      const date = new Date(ts);
      return date.toLocaleString();
    } catch {
      return ts;
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      {/* Main Quality Indicator */}
      <Alert 
        severity={qualityLevel.color as any}
        icon={qualityLevel.icon}
        sx={{ mb: 2 }}
      >
        <AlertTitle>Data Quality: {(confidence * 100).toFixed(0)}%</AlertTitle>
        <Box sx={{ mt: 1 }}>
          <LinearProgress 
            variant="determinate" 
            value={confidence * 100} 
            color={qualityLevel.color as any}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
      </Alert>

      {/* Data Sources */}
      {sources.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            Data Sources Used:
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {sources.map((source, index) => (
              <Chip
                key={index}
                label={formatSource(source)}
                size="small"
                color={
                  source.includes('municipal') || source === 'oauth2' || source === 'l3' ? 'primary' : 
                  source === 'market' || source === 'public' ? 'success' :
                  source === 'ml_predictor' ? 'secondary' :
                  'default'
                }
                variant={source.includes('default') || source.includes('fallback') ? 'outlined' : 'filled'}
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* Missing Fields Warning */}
      {missingFields.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Missing Data Fields</AlertTitle>
          <Typography variant="body2">
            Some data fields could not be retrieved:
          </Typography>
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
            {missingFields.map((field, index) => (
              <li key={index}>
                <Typography variant="body2">{field.replace(/_/g, ' ')}</Typography>
              </li>
            ))}
          </Box>
        </Alert>
      )}

      {/* Data Freshness */}
      {timestamp && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="textSecondary">
            <InfoIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
            Data retrieved: {formatTimestamp(timestamp)}
          </Typography>
        </Box>
      )}

      {/* Quality Explanation */}
      <Box sx={{ mt: 2 }}>
        <Tooltip title={
          <Box>
            <Typography variant="body2" paragraph>
              Data quality is based on:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>Source reliability (Municipal GIS is most trusted)</li>
              <li>Data completeness (fewer missing fields = higher score)</li>
              <li>Data freshness (recent data scores higher)</li>
              <li>Multiple source confirmation</li>
            </ul>
          </Box>
        }>
          <Typography 
            variant="caption" 
            color="primary" 
            sx={{ cursor: 'help', textDecoration: 'underline dotted' }}
          >
            How is data quality calculated?
          </Typography>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default DataQualityIndicator;